import { useEffect, useRef, useState } from 'react';

export interface ProgramPanelProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  children: React.ReactNode;
  hotkeysDisabled?: boolean;
}

function useIsDesktop(): boolean {
  const [desktop, setDesktop] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const h = () => setDesktop(mq.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  return desktop;
}

export function ProgramPanel({ open, onOpenChange, children, hotkeysDisabled }: ProgramPanelProps) {
  const desktop = useIsDesktop();
  const asideRef = useRef<HTMLElement>(null);
  const propsRef = useRef({ open, onOpenChange, hotkeysDisabled });
  propsRef.current = { open, onOpenChange, hotkeysDisabled };

  useEffect(() => {
    if (open) asideRef.current?.focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.isComposing || e.keyCode === 229) return;
      const p = propsRef.current;
      if (p.hotkeysDisabled) return;
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el as HTMLElement | null)?.isContentEditable) {
        return;
      }
      if (e.key === 'Tab') {
        if (el !== document.body && el !== null && el !== asideRef.current) return;
        e.preventDefault();
        p.onOpenChange(!p.open);
      } else if (e.key === 'Escape' && p.open) {
        e.preventDefault();
        p.onOpenChange(false);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const desktopTransform = open ? 'translateX(0)' : 'translateX(calc(100% + 2rem))';
  const mobileTransform = open ? 'translateY(0)' : 'translateY(calc(100% + 1.5rem))';

  return (
    <>
      {open && (
        <div
          className={`absolute inset-0 z-10 ${desktop ? '' : 'bg-black/30 transition-opacity duration-200'}`}
          onClick={() => onOpenChange(false)}
        />
      )}

      <aside
        ref={asideRef}
        role="complementary"
        aria-label="节目志"
        aria-hidden={!open}
        tabIndex={-1}
        className={
          desktop
            ? 'glass glass-strong sheet-spring absolute inset-y-4 right-4 z-20 w-[min(26rem,92vw)] overflow-hidden rounded-[24px]'
            : 'glass glass-strong sheet-spring absolute inset-x-2 bottom-[max(0.5rem,env(safe-area-inset-bottom))] z-20 max-h-[72dvh] overflow-hidden rounded-[24px]'
        }
        style={{
          transform: desktop ? desktopTransform : mobileTransform,
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        {!desktop && <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-fg/20" />}
        <div className="h-full max-h-[calc(72dvh-1rem)] overflow-y-auto overscroll-contain md:max-h-none">
          {children}
        </div>
      </aside>

      <button
        aria-label="打开节目志 (Tab)"
        onClick={() => onOpenChange(true)}
        onPointerEnter={(e) => {
          if (e.pointerType === 'mouse') onOpenChange(true);
        }}
        className={`glass absolute right-0 top-1/2 z-20 hidden h-24 w-4 -translate-y-1/2 cursor-pointer place-items-center rounded-l-2xl rounded-r-none border-r-0 transition-opacity duration-200 md:grid ${
          open ? 'pointer-events-none opacity-0' : 'opacity-100'
        }`}
      >
        <span className="h-6 w-px bg-fg/40" />
      </button>

      <button
        aria-label="打开节目志"
        onClick={() => onOpenChange(true)}
        className={`glass absolute bottom-[max(1rem,env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] z-20 grid h-9 w-9 place-items-center rounded-full md:hidden ${
          open ? 'pointer-events-none opacity-0' : 'opacity-100'
        }`}
      >
        ≡
      </button>
    </>
  );
}
